import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';
import type { QuotaTenant } from '@/services/adminMetricsService';

interface QuotaPressureListProps {
  tenants: QuotaTenant[];
}

/**
 * List of tenants approaching or exceeding quota.
 * Each row has an inline progressbar; >80% = warning colour.
 * EmptyState when no high-pressure tenants.
 */
export function QuotaPressureList({ tenants }: QuotaPressureListProps) {
  const { t } = useTranslation();

  if (tenants.length === 0) {
    return (
      <div data-testid="quota-empty">
        <EmptyState
          icon={BarChart3}
          title={t('admin.metrics.quota.empty_title')}
          subtitle={t('admin.metrics.quota.empty_body')}
        />
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {tenants.map((tenant) => {
        const atRisk = tenant.used_pct >= 80;
        return (
          <li key={tenant.tenant_id} className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate font-medium">{tenant.business_name}</span>
              <span
                className={cn(
                  'shrink-0 tabular-nums text-xs',
                  atRisk ? 'text-warning font-semibold' : 'text-muted-foreground'
                )}
              >
                {tenant.messages_used_current_period}/{tenant.monthly_message_quota} (
                {tenant.used_pct}%)
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                role="progressbar"
                aria-valuenow={tenant.used_pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${tenant.business_name} quota usage`}
                className={cn(
                  'h-full rounded-full transition-all',
                  atRisk ? 'bg-warning' : 'bg-foreground'
                )}
                style={{ width: `${Math.min(tenant.used_pct, 100)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{tenant.subscription_tier}</p>
          </li>
        );
      })}
    </ul>
  );
}
